/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument */
import React, {
  useState,
  useEffect,
  ReactElement,
  useCallback,
  useRef,
} from 'react'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  BarChart,
  Bar,
  Line,
} from 'recharts'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

import Page from '../Shared/Page'
import { aiAssistantAxios } from '../../utils/io/http/axios'
import './AIAnalytics.scss'

// ─── Types ────────────────────────────────────────────────────────────────────

interface EngagementTotals {
  total_conversations: number
  unique_users: number
  total_messages: number
}

interface EngagementAverages {
  avg_messages_per_conversation: number
  max_messages_per_conversation: number
  min_messages_per_conversation: number
}

interface DailyEngagement {
  date: string
  conversations: number
  avg_messages_per_conversation: number
}

interface TopUser {
  userId: number
  email: string
  name: string
  conversation_count: number
  message_count: number
}

interface PerApp {
  appId: string
  app_name: string
  conversations: number
  messages: number
}

interface EngagementData {
  period_days: number
  totals: EngagementTotals
  averages: EngagementAverages
  daily: DailyEngagement[]
  top_users: TopUser[]
  top_users_pagination: Pagination
  per_app: PerApp[]
  per_app_pagination: Pagination
}

interface ErrorSummary {
  total_requests: number
  successful_requests: number
  error_requests: number
  error_rate_pct: number
}

interface DailyError {
  date: string
  total_requests: number
  successful_requests: number
  error_requests: number
  error_rate_pct: number
}

interface TopError {
  error_snippet: string
  occurrences: number
}

interface ErrorData {
  period_days: number
  summary: ErrorSummary
  daily: DailyError[]
  top_errors: TopError[]
}

interface SatisfactionOverall {
  total_ratings: number
  thumbs_up_count: number
  thumbs_down_count: number
  satisfaction_rate_pct: number
}

interface DailySatisfaction {
  date: string
  total_ratings: number
  thumbs_up: number
  thumbs_down: number
}

interface SatisfactionData {
  period_days: number
  overall: SatisfactionOverall
  daily: DailySatisfaction[]
}

interface WeeklyUserStat {
  week_start: string
  total_users: number
  new_users: number
  churned_users: number
}

interface WeeklyUsersData {
  period_days: number
  weeks: number
  data: WeeklyUserStat[]
}

interface AppCreationStat {
  date: string
  total: number
  successful: number
  unsuccessful: number
}

interface AppCreationData {
  period_days: number
  bucket_type: 'daily' | 'weekly'
  data: AppCreationStat[]
}

interface FeedbackItem {
  id: string
  conversationId: string
  userId: number
  appId: string
  rating: 'thumbs_up' | 'thumbs_down'
  feedback: string | null
  createdAt: string
  user_email: string
  user_name: string
  app_name: string
  conversation_type: string
}

interface FeedbackListData {
  data: FeedbackItem[]
  pagination: { offset: number; limit: number; total: number }
  counts: {
    total: number
    thumbs_up: number
    thumbs_down: number
  }
}

interface Pagination {
  offset: number
  limit: number
  total: number
}

interface ConversationMessage {
  id: string
  conversationId: string
  role: 'user' | 'assistant'
  message: string
  statuses: string | null
  error: string | null
  action: string | null
  createdAt: string
}

interface AppConversation {
  id: string
  type: string
  userId: number
  createdAt: string
  user_email: string
  user_name: string
  message_count: number
  user_messages: number
  assistant_messages: number
  error_count: number
  feedback_rating: 'thumbs_up' | 'thumbs_down' | null
}

interface ConversationFeedbackInfo {
  rating: 'thumbs_up' | 'thumbs_down'
  feedback: string | null
  createdAt: string
}

interface AppConversationsData {
  data: AppConversation[]
  pagination: { offset: number; limit: number; total: number }
}

// ─── Component ────────────────────────────────────────────────────────────────

const FEEDBACK_PAGE_SIZE = 30
const TOP_USERS_PAGE_SIZE = 20
const PER_APP_PAGE_SIZE = 20

const AIAnalytics = (): ReactElement => {
  const [days, setDays] = useState(30)
  const [loading, setLoading] = useState(true)
  const [loadingTopUsers, setLoadingTopUsers] = useState(false)
  const [loadingPerApp, setLoadingPerApp] = useState(false)
  const [loadingFeedback, setLoadingFeedback] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)

  // Engagement
  const [engagement, setEngagement] = useState<EngagementData | null>(null)

  // Error Rate
  const [errorRate, setErrorRate] = useState<ErrorData | null>(null)

  // Satisfaction
  const [satisfaction, setSatisfaction] = useState<SatisfactionData | null>(null)
  const [weeklyUsers, setWeeklyUsers] = useState<WeeklyUsersData | null>(null)

  // AI Application Creation
  const [appCreation, setAppCreation] = useState<AppCreationData | null>(null)

  // Feedback list
  const [feedbackTab, setFeedbackTab] = useState<
    'thumbs_up' | 'thumbs_down' | 'all'
  >('all')
  const [feedbackList, setFeedbackList] = useState<FeedbackListData | null>(
    null
  )
  const [feedbackPage, setFeedbackPage] = useState(1)

  // Top users pagination
  const [topUsersPage, setTopUsersPage] = useState(1)

  // Per-app pagination
  const [perAppPage, setPerAppPage] = useState(1)

  // Expanded app conversations
  const [expandedApp, setExpandedApp] = useState<string | null>(null)
  const [appConversations, setAppConversations] = useState<
    Record<string, AppConversationsData>
  >({})
  const [loadingApp, setLoadingApp] = useState<string | null>(null)

  // Expanded conversation messages (used in both app drill-down and feedback)
  const [expandedConversation, setExpandedConversation] = useState<
    string | null
  >(null)
  const [conversationMessages, setConversationMessages] = useState<
    Record<string, ConversationMessage[]>
  >({})
  const [conversationFeedback, setConversationFeedback] = useState<
    Record<string, ConversationFeedbackInfo | null>
  >({})
  const [loadingConversation, setLoadingConversation] = useState<string | null>(
    null
  )
  const topUsersTableRef = useRef<HTMLDivElement | null>(null)
  const perAppTableRef = useRef<HTMLDivElement | null>(null)
  const feedbackListRef = useRef<HTMLDivElement | null>(null)
  const reportRef = useRef<HTMLDivElement | null>(null)
  const pendingScrollTopUsers = useRef(false)
  const pendingScrollPerApp = useRef(false)
  const pendingScrollFeedback = useRef(false)

  const fetchAll = useCallback(
    async (options?: {
      showPageLoader?: boolean
      showTopUsersLoader?: boolean
      showPerAppLoader?: boolean
    }) => {
      const {
        showPageLoader = false,
        showTopUsersLoader = false,
        showPerAppLoader = false,
      } = options || {}

      if (showPageLoader) {
        setLoading(true)
        setError(null)
      }
      if (showTopUsersLoader) {
        setLoadingTopUsers(true)
      }
      if (showPerAppLoader) {
        setLoadingPerApp(true)
      }

      try {
        const topUsersOffset = (topUsersPage - 1) * TOP_USERS_PAGE_SIZE
        const perAppOffset = (perAppPage - 1) * PER_APP_PAGE_SIZE
        const engagementRequest = aiAssistantAxios.get<EngagementData>(
          `/admin/ai/engagement?days=${days}&top_users_offset=${topUsersOffset}&top_users_limit=${TOP_USERS_PAGE_SIZE}&per_app_offset=${perAppOffset}&per_app_limit=${PER_APP_PAGE_SIZE}`
        )

        if (showPageLoader) {
          const [engRes, errRes, satRes, weeklyRes, appCreationRes] =
            await Promise.all([
            engagementRequest,
            aiAssistantAxios.get<ErrorData>(`/admin/ai/error-rate?days=${days}`),
            aiAssistantAxios.get<SatisfactionData>(
              `/admin/ai/satisfaction?days=${days}`
            ),
            aiAssistantAxios.get<WeeklyUsersData>(
              `/admin/ai/weekly-users?days=${days}`
            ),
            aiAssistantAxios.get<AppCreationData>(
              `/admin/ai/app-creation?days=${days}`
            ),
          ])
          setEngagement(engRes.data)
          setErrorRate(errRes.data)
          setSatisfaction(satRes.data)
          setWeeklyUsers(weeklyRes.data)
          setAppCreation(appCreationRes.data)
        } else {
          const { data } = await engagementRequest
          setEngagement(data)
        }
      } catch (err) {
        console.error('Failed to fetch AI analytics', err)
        if (showPageLoader) {
          setError('Failed to load AI analytics data.')
        }
      } finally {
        if (showPageLoader) {
          setLoading(false)
        }
        if (showTopUsersLoader) {
          setLoadingTopUsers(false)
        }
        if (showPerAppLoader) {
          setLoadingPerApp(false)
        }
      }
    },
    [days, topUsersPage, perAppPage]
  )

  const fetchFeedback = useCallback(async () => {
    setLoadingFeedback(true)
    try {
      const offset = (feedbackPage - 1) * FEEDBACK_PAGE_SIZE
      const ratingParam =
        feedbackTab === 'all' ? '' : `&rating=${feedbackTab}`
      const { data } = await aiAssistantAxios.get<FeedbackListData>(
        `/admin/ai/feedback?offset=${offset}&limit=${FEEDBACK_PAGE_SIZE}&days=${days}${ratingParam}`
      )
      setFeedbackList(data)
    } catch (err) {
      console.error('Failed to fetch feedback list', err)
    } finally {
      setLoadingFeedback(false)
    }
  }, [feedbackTab, feedbackPage, days])

  const previousPagination = useRef<{
    days: number
    topUsersPage: number
    perAppPage: number
  } | null>(null)

  useEffect(() => {
    const previous = previousPagination.current
    const isInitialLoad = !previous
    const daysChanged = previous ? previous.days !== days : true
    const topUsersChanged = previous
      ? previous.topUsersPage !== topUsersPage
      : false
    const perAppChanged = previous
      ? previous.perAppPage !== perAppPage
      : false

    previousPagination.current = { days, topUsersPage, perAppPage }

    const showPageLoader = isInitialLoad || daysChanged
    const showTopUsersLoader = !showPageLoader && topUsersChanged
    const showPerAppLoader = !showPageLoader && perAppChanged

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchAll({
      showPageLoader,
      showTopUsersLoader,
      showPerAppLoader,
    })
  }, [days, topUsersPage, perAppPage, fetchAll])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchFeedback()
  }, [fetchFeedback])

  const handleDaysChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDays(parseInt(e.target.value, 10))
    setTopUsersPage(1)
    setPerAppPage(1)
    setExpandedApp(null)
    setExpandedConversation(null)
    setAppConversations({})
  }

  const handleFeedbackTabChange = (
    tab: 'thumbs_up' | 'thumbs_down' | 'all'
  ) => {
    setFeedbackTab(tab)
    setFeedbackPage(1)
    setExpandedConversation(null)
  }

  const scrollToRef = useCallback((ref: React.RefObject<HTMLDivElement>) => {
    if (!ref.current) return
    const navbar = document.querySelector<HTMLElement>('.app-navbar')
    const navbarOffset = navbar
      ? navbar.getBoundingClientRect().height + navbar.getBoundingClientRect().top
      : 0
    const targetTop =
      window.scrollY + ref.current.getBoundingClientRect().top
    const scrollTarget = Math.max(targetTop - navbarOffset - 8, 0)

    window.scrollTo({ top: scrollTarget, behavior: 'smooth' })
  }, [])

  const handleTopUsersPageChange = (page: number) => {
    setTopUsersPage(page)
    pendingScrollTopUsers.current = true
  }

  const handlePerAppPageChange = (page: number) => {
    setPerAppPage(page)
    setExpandedApp(null)
    setExpandedConversation(null)
    pendingScrollPerApp.current = true
  }

  const handleFeedbackPageChange = (page: number) => {
    setFeedbackPage(page)
    pendingScrollFeedback.current = true
  }

  const handleExportPdf = async () => {
    if (!reportRef.current || isExporting) return

    setIsExporting(true)
    try {
      const pdfPadding = 30
      const reportElement = reportRef.current
      const reportRect = reportElement.getBoundingClientRect()
      const reportTop = reportRect.top + window.scrollY
      const reportHeight = reportElement.scrollHeight
      const chartElements = Array.from(
        reportElement.querySelectorAll<HTMLElement>('.chart-container')
      )
      const chartRanges = chartElements
        .map((element) => {
          const rect = element.getBoundingClientRect()
          const top = rect.top + window.scrollY - reportTop
          const bottom = top + rect.height
          
          return { top: Math.max(top, 0), bottom: Math.min(bottom, reportHeight) }
        })
        .filter((range) => range.bottom > range.top)
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: reportRef.current.scrollWidth,
        windowHeight: reportRef.current.scrollHeight,
        ignoreElements: (element) => element.classList?.contains('export-button') ?? false,
      })
      const imgData = canvas.toDataURL('image/png')
      // eslint-disable-next-line new-cap
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'pt',
        format: 'a4',
      })
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const contentWidth = pageWidth - pdfPadding * 2
      const contentHeight = pageHeight - pdfPadding * 2
      const imgWidth = contentWidth
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      const scaleToPdf = imgWidth / canvas.width
      const pageHeightCanvas = contentHeight / scaleToPdf
      const chartTopGapPdf = 30
      const chartTopGapCanvas = chartTopGapPdf / scaleToPdf
      const chartRangesCanvas = chartRanges
        .map((range) => ({
          top: range.top * (canvas.height / reportHeight),
          bottom: range.bottom * (canvas.height / reportHeight),
        }))
        .sort((a, b) => a.top - b.top)

      const pageBreaks: number[] = []
      let current = 0
      while (current < canvas.height - 1) {
        let nextBreak = current + pageHeightCanvas
        if (nextBreak >= canvas.height) {
          pageBreaks.push(canvas.height)
          
          break
        }
        const blockingChart = chartRangesCanvas.find(
          (range) => range.top < nextBreak && range.bottom > nextBreak
        )
        if (blockingChart && blockingChart.top > current + 1) {
          nextBreak = blockingChart.top
        }
        const nearBottomChart = chartRangesCanvas.find(
          // eslint-disable-next-line @typescript-eslint/no-loop-func
          (range) =>
            range.top > current + 1 &&
            range.top < nextBreak &&
            nextBreak - range.top < chartTopGapCanvas
        )
        if (nearBottomChart) {
          nextBreak = nearBottomChart.top
        }
        if (nextBreak <= current + 1) {
          nextBreak = Math.min(current + pageHeightCanvas, canvas.height)
        }
        pageBreaks.push(nextBreak)
        current = nextBreak
      }

      let pageStart = 0
      pageBreaks.forEach((breakPoint, index) => {
        const sliceHeight = Math.max(breakPoint - pageStart, 1)
        const sliceCanvas = document.createElement('canvas')
        sliceCanvas.width = canvas.width
        sliceCanvas.height = sliceHeight
        const sliceContext = sliceCanvas.getContext('2d')
        if (sliceContext) {
          sliceContext.drawImage(
            canvas,
            0,
            pageStart,
            canvas.width,
            sliceHeight,
            0,
            0,
            canvas.width,
            sliceHeight
          )
        }

        const sliceData = sliceCanvas.toDataURL('image/png')
        const sliceHeightPdf = sliceHeight * scaleToPdf
        if (index > 0) {
          pdf.addPage()
        }
        pdf.addImage(
          sliceData,
          'PNG',
          pdfPadding,
          pdfPadding,
          imgWidth,
          sliceHeightPdf
        )
        pageStart = breakPoint
      })

      const dateStamp = new Date().toISOString().slice(0, 10)
      pdf.save(`ai-analytics-last-${days}-days-${dateStamp}.pdf`)
    } catch (err) {
      console.error('Failed to export AI analytics PDF', err)
    } finally {
      setIsExporting(false)
    }
  }

  useEffect(() => {
    if (!loadingTopUsers && pendingScrollTopUsers.current) {
      pendingScrollTopUsers.current = false
      scrollToRef(topUsersTableRef)
    }
  }, [loadingTopUsers, scrollToRef])

  useEffect(() => {
    if (!loadingPerApp && pendingScrollPerApp.current) {
      pendingScrollPerApp.current = false
      scrollToRef(perAppTableRef)
    }
  }, [loadingPerApp, scrollToRef])

  useEffect(() => {
    if (!loadingFeedback && pendingScrollFeedback.current) {
      pendingScrollFeedback.current = false
      scrollToRef(feedbackListRef)
    }
  }, [loadingFeedback, scrollToRef])

  const handleToggleApp = async (appId: string) => {
    if (expandedApp === appId) {
      setExpandedApp(null)
      setExpandedConversation(null)

      return
    }

    setExpandedApp(appId)
    setExpandedConversation(null)

    if (appConversations[appId]) return

    setLoadingApp(appId)
    try {
      const { data } = await aiAssistantAxios.get<AppConversationsData>(
        `/admin/ai/app/${appId}/conversations?limit=50&days=${days}`
      )
      setAppConversations(prev => ({ ...prev, [appId]: data }))
    } catch (err) {
      console.error('Failed to fetch app conversations', err)
    } finally {
      setLoadingApp(null)
    }
  }

  const handleToggleConversation = async (conversationId: string) => {
    if (expandedConversation === conversationId) {
      setExpandedConversation(null)

      return
    }

    setExpandedConversation(conversationId)

    // Already loaded
    if (conversationMessages[conversationId]) return

    setLoadingConversation(conversationId)
    try {
      const { data } = await aiAssistantAxios.get<{
        messages: ConversationMessage[]
        feedback: ConversationFeedbackInfo | null
      }>(`/admin/ai/conversation/${conversationId}/messages`)
      setConversationMessages(prev => ({
        ...prev,
        [conversationId]: data.messages,
      }))
      setConversationFeedback(prev => ({
        ...prev,
        [conversationId]: data.feedback,
      }))
    } catch (err) {
      console.error('Failed to fetch conversation messages', err)
    } finally {
      setLoadingConversation(null)
    }
  }

  const parseStatuses = (
    statusesRaw: string | null
  ): Array<{ type: string; message: string; action?: string }> => {
    if (!statusesRaw) return []
    try {
      const parsed = JSON.parse(statusesRaw) as unknown[]

      return Array.isArray(parsed) ? (parsed as Array<{ type: string; message: string; action?: string }>) : []
    } catch {
      return []
    }
  }

  const getResponseTimeSeconds = (
    messages: ConversationMessage[],
    idx: number
  ): number | null => {
    const msg = messages[idx]
    if (!msg || msg.role !== 'assistant') return null

    for (let i = idx - 1; i >= 0; i -= 1) {
      const previous = messages[i]
      if (!previous) continue
      if (previous.role === 'user') {
        const diffMs =
          new Date(msg.createdAt).getTime() -
          new Date(previous.createdAt).getTime()

        if (!Number.isFinite(diffMs) || diffMs < 0) return null

        return Math.round(diffMs / 1000)
      }
    }

    return null
  }

  const formatResponseTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60

    return `${mins}m ${secs}s`
  }

  const getResponseTimeClass = (seconds: number) => {
    if (seconds > 60) return 'slow'
    if (seconds > 30) return 'medium'

    return 'fast'
  }

  const engagementDaily = (() => {
    const daily = engagement?.daily ?? []
    if (daily.length === 0) return []

    const dailyByDate = new Map(daily.map(entry => [entry.date, entry]))
    const now = new Date()
    const totalDays = Math.max(days, 1)

    const getWeekKey = (date: Date) => {
      const utc = new Date(
        Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
      )
      const day = utc.getUTCDay() || 7
      utc.setUTCDate(utc.getUTCDate() + 4 - day)
      const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1))
      const weekNo = Math.ceil(
        ((utc.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
      )

      return `${utc.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`
    }

    if (days >= 60) {
      const weekTotals = new Map<
        string,
        { conversations: number; msgSum: number; count: number }
      >()
      const orderedWeeks: string[] = []

      for (let i = totalDays - 1; i >= 0; i -= 1) {
        const date = new Date(now)
        date.setUTCDate(date.getUTCDate() - i)
        const dateKey = date.toISOString().slice(0, 10)
        const weekKey = getWeekKey(date)
        const entry = dailyByDate.get(dateKey)

        if (!weekTotals.has(weekKey)) {
          weekTotals.set(weekKey, { conversations: 0, msgSum: 0, count: 0 })
          orderedWeeks.push(weekKey)
        }

        if (entry) {
          const totals = weekTotals.get(weekKey)!
          totals.conversations += entry.conversations
          totals.msgSum += entry.avg_messages_per_conversation
          totals.count += 1
        }
      }

      return orderedWeeks.map(weekKey => {
        const totals = weekTotals.get(weekKey)!

        return {
          date: weekKey,
          conversations: totals.conversations,
          avg_messages_per_conversation: totals.count
            ? Number((totals.msgSum / totals.count).toFixed(2))
            : 0,
        }
      })
    }

    const series: DailyEngagement[] = []

    for (let i = totalDays - 1; i >= 0; i -= 1) {
      const date = new Date(now)
      date.setUTCDate(date.getUTCDate() - i)
      const dateKey = date.toISOString().slice(0, 10)
      const entry = dailyByDate.get(dateKey)

      series.push(
        entry ?? {
          date: dateKey,
          conversations: 0,
          avg_messages_per_conversation: 0,
        }
      )
    }

    return series
  })()

  const weeklyUsersSeries = (weeklyUsers?.data ?? []).map(entry => ({
    ...entry,
    week_label: new Date(entry.week_start).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
  }))

  const appCreationSeries = (appCreation?.data ?? []).map(entry => ({
    ...entry,
    label: new Date(entry.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
  }))

  const feedbackTotalPages = feedbackList
    ? Math.ceil(feedbackList.pagination.total / FEEDBACK_PAGE_SIZE)
    : 0
  const feedbackCounts = feedbackList?.counts
  const feedbackAllCount =
    feedbackCounts?.total ?? feedbackList?.pagination.total ?? 0
  const feedbackThumbsUpCount = feedbackCounts?.thumbs_up
  const feedbackThumbsDownCount = feedbackCounts?.thumbs_down

  const topUsersTotalPages = engagement?.top_users_pagination
    ? Math.ceil(
        engagement.top_users_pagination.total /
          engagement.top_users_pagination.limit
      )
    : 0

  const perAppTotalPages = engagement?.per_app_pagination
    ? Math.ceil(
        engagement.per_app_pagination.total /
          engagement.per_app_pagination.limit
      )
    : 0

  if (loading) {
    return (
      <Page className="ai-analytics-page" title="AI Analytics">
        <div className="loading-container">Loading AI analytics...</div>
      </Page>
    )
  }

  return (
    <Page className="ai-analytics-page" title="AI Analytics">
      <div className="ai-analytics-content" ref={reportRef}>
        <div className="ai-analytics-header">
          <h1>AI Assistant Analytics</h1>
          <div className="header-actions">
            <div className="period-selector">
              <label htmlFor="days-select">Period:</label>
              <select
                id="days-select"
                value={days}
                onChange={handleDaysChange}
              >
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
                <option value={60}>Last 60 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </div>
            <button
              type="button"
              className="export-button"
              onClick={() => {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                handleExportPdf()
              }}
              disabled={isExporting}
            >
              {isExporting ? 'Exporting PDF...' : 'Export PDF'}
            </button>
          </div>
        </div>

        {error && <div className="error-banner">{error}</div>}

      {/* ─── Engagement Section ────────────────────────────────── */}
      <div className="analytics-section">
        <h2 className="section-title">User Engagement & Prompting Data</h2>

        <div className="stats-cards">
          <div className="stat-card info">
            <div className="stat-label">Total Conversations</div>
            <div className="stat-value">
              {engagement?.totals?.total_conversations ?? 0}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Unique Users</div>
            <div className="stat-value">
              {engagement?.totals?.unique_users ?? 0}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Messages</div>
            <div className="stat-value">
              {engagement?.totals?.total_messages ?? 0}
            </div>
          </div>
          <div className="stat-card info">
            <div className="stat-label">Avg Messages/Conv</div>
            <div className="stat-value">
              {engagement?.averages?.avg_messages_per_conversation ?? 0}
            </div>
          </div>
        </div>

        <div className="charts-container">
          <div className="chart-container">
            <h2>Daily Conversations & Avg Length</h2>
            <p>Last {days} days</p>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={engagementDaily}
                margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, angle: -60, textAnchor: 'end' }}
                  interval={0}
                  minTickGap={0}
                  height={70}
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="conversations"
                  fill="#6366f1"
                  name="Conversations"
                />
                <Bar
                  dataKey="avg_messages_per_conversation"
                  fill="#10b981"
                  name="Avg Messages/Conversation"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Users Table */}
        {engagement?.top_users && engagement.top_users.length > 0 && (
          <>
            <div className="analytics-table-wrapper" ref={topUsersTableRef}>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th style={{ textAlign: 'right' }}>Conversations</th>
                    <th style={{ textAlign: 'right' }}>Messages</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingTopUsers ? (
                    <tr className="loading-row">
                      <td colSpan={4}>
                        <div className="conversation-loading">
                          Loading users...
                        </div>
                      </td>
                    </tr>
                  ) : (
                    engagement.top_users.map(u => (
                      <tr key={u.userId}>
                        <td>{u.name || '—'}</td>
                        <td>{u.email || '—'}</td>
                        <td className="numeric">{u.conversation_count}</td>
                        <td className="numeric">{u.message_count}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {topUsersTotalPages > 1 && (
              <div className="ai-pagination">
                <div className="pagination-info">
                  Page {topUsersPage} of {topUsersTotalPages} (
                  {engagement.top_users_pagination.total} total)
                </div>
                <div className="pagination-controls">
                  <button
                    type="button"
                    disabled={topUsersPage === 1 || loadingTopUsers}
                    onClick={() => handleTopUsersPageChange(1)}
                  >
                    First
                  </button>
                  <button
                    type="button"
                    disabled={topUsersPage === 1 || loadingTopUsers}
                    onClick={() => handleTopUsersPageChange(topUsersPage - 1)}
                  >
                    Prev
                  </button>
                  <span className="page-info">
                    {topUsersPage} / {topUsersTotalPages}
                  </span>
                  <button
                    type="button"
                    disabled={
                      topUsersPage === topUsersTotalPages || loadingTopUsers
                    }
                    onClick={() => handleTopUsersPageChange(topUsersPage + 1)}
                  >
                    Next
                  </button>
                  <button
                    type="button"
                    disabled={
                      topUsersPage === topUsersTotalPages || loadingTopUsers
                    }
                    onClick={() => handleTopUsersPageChange(topUsersTotalPages)}
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Per-App Table */}
        {engagement?.per_app && engagement.per_app.length > 0 && (
          <div className="app-conversations-list" ref={perAppTableRef}>
            <div className="analytics-table-wrapper">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th />
                    <th>App</th>
                    <th style={{ textAlign: 'right' }}>Conversations</th>
                    <th style={{ textAlign: 'right' }}>Messages</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingPerApp ? (
                    <tr className="loading-row">
                      <td colSpan={4}>
                        <div className="conversation-loading">
                          Loading apps...
                        </div>
                      </td>
                    </tr>
                  ) : (
                    engagement.per_app.map(a => {
                      const isAppExpanded = expandedApp === a.appId
                      const convData = appConversations[a.appId]
                      const isAppLoading = loadingApp === a.appId

                      return (
                        <React.Fragment key={a.appId}>
                          <tr
                            className="clickable-row"
                            onClick={() => {
                              // eslint-disable-next-line @typescript-eslint/no-floating-promises
                              handleToggleApp(a.appId)
                            }}
                          >
                            <td style={{ width: 30 }}>
                              <span
                                className={`row-expand-icon ${isAppExpanded ? 'open' : ''}`}
                              >
                                &#9662;
                              </span>
                            </td>
                            <td>
                              <strong>{a.app_name || a.appId}</strong>
                            </td>
                            <td className="numeric">{a.conversations}</td>
                            <td className="numeric">{a.messages}</td>
                          </tr>

                          {isAppExpanded && (
                            <tr className="expanded-row">
                              <td colSpan={4} style={{ padding: 0 }}>
                                <div className="app-conversations-panel">
                                  {isAppLoading && (
                                    <div className="conversation-loading">
                                      Loading conversations...
                                    </div>
                                  )}
                                  {convData &&
                                    convData.data.map(conv => {
                                      const isConvExpanded =
                                        expandedConversation === conv.id
                                      const msgs =
                                        conversationMessages[conv.id]
                                      const isMsgLoading =
                                        loadingConversation === conv.id

                                      return (
                                        <div
                                          key={conv.id}
                                          className={`app-conversation-item ${isConvExpanded ? 'expanded' : ''}`}
                                        >
                                          <div
                                            className="app-conversation-header"
                                            onClick={e => {
                                              e.stopPropagation()
                                              // eslint-disable-next-line @typescript-eslint/no-floating-promises
                                              handleToggleConversation(conv.id)
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={e => {
                                              if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                              ) {
                                                e.stopPropagation()
                                                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                                                handleToggleConversation(
                                                  conv.id
                                                )
                                              }
                                            }}
                                          >
                                            <span
                                              className={`row-expand-icon small ${isConvExpanded ? 'open' : ''}`}
                                            >
                                              &#9662;
                                            </span>
                                            <div className="app-conversation-info">
                                              <span className="app-conversation-user">
                                                {conv.user_name || 'Unknown'}{' '}
                                                <span className="app-conversation-email">
                                                  ({conv.user_email || '—'})
                                                </span>
                                              </span>
                                              <span className="app-conversation-stats">
                                                {conv.message_count} msgs
                                                {conv.error_count > 0 && (
                                                  <span className="app-conversation-errors">
                                                    {conv.error_count} errors
                                                  </span>
                                                )}
                                                {conv.feedback_rating && (
                                                  <span
                                                    className={`app-conversation-feedback ${conv.feedback_rating}`}
                                                  >
                                                    {conv.feedback_rating ===
                                                    'thumbs_up'
                                                      ? '👍'
                                                      : '👎'}
                                                  </span>
                                                )}
                                              </span>
                                            </div>
                                            <span className="app-conversation-date">
                                              {new Date(
                                                conv.createdAt
                                              ).toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                              })}
                                            </span>
                                          </div>

                                          {isConvExpanded && (
                                            <div className="conversation-thread">
                                              {isMsgLoading && (
                                                <div className="conversation-loading">
                                                  Loading messages...
                                                </div>
                                              )}
                                              {msgs && msgs.length > 0 && (
                                                <div className="conversation-messages">
                                                  {msgs.map((msg, idx) => {
                                                    const statuses =
                                                      parseStatuses(
                                                        msg.statuses
                                                      )
                                                    const responseSeconds =
                                                      getResponseTimeSeconds(
                                                        msgs,
                                                        idx
                                                      )

                                                    return (
                                                      <div
                                                        key={msg.id}
                                                        className={`conversation-msg ${msg.role}`}
                                                      >
                                                        <div className="conversation-msg-header">
                                                          <span
                                                            className={`conversation-msg-role ${msg.role}`}
                                                          >
                                                            {msg.role ===
                                                            'user'
                                                              ? 'User'
                                                              : 'AI Assistant'}
                                                          </span>
                                                          <span className="conversation-msg-time">
                                                            {new Date(
                                                              msg.createdAt
                                                            ).toLocaleTimeString(
                                                              'en-US',
                                                              {
                                                                hour: '2-digit',
                                                                minute:
                                                                  '2-digit',
                                                              }
                                                            )}
                                                          </span>
                                                          {responseSeconds !==
                                                            null && (
                                                            <span
                                                              className={`conversation-msg-response-time ${getResponseTimeClass(
                                                                responseSeconds
                                                              )}`}
                                                            >
                                                              Response:{' '}
                                                              {formatResponseTime(
                                                                responseSeconds
                                                              )}
                                                            </span>
                                                          )}
                                                          {msg.error && (
                                                            <span className="conversation-msg-error-badge">
                                                              Error
                                                            </span>
                                                          )}
                                                        </div>
                                                        <div className="conversation-msg-body">
                                                          {msg.message}
                                                        </div>
                                                        {statuses.length >
                                                          0 && (
                                                          <div className="conversation-msg-statuses">
                                                            {statuses.map(
                                                              s => (
                                                                <span
                                                                  key={`${msg.id}-${s.type}-${s.message.slice(0, 20)}`}
                                                                  className={`conversation-status-badge status-${s.type}`}
                                                                >
                                                                  {s.type ===
                                                                    'success' &&
                                                                    '✓ '}
                                                                  {s.type ===
                                                                    'error' &&
                                                                    '✕ '}
                                                                  {s.type ===
                                                                    'info' &&
                                                                    'ℹ '}
                                                                  {s.message}
                                                                </span>
                                                              )
                                                            )}
                                                          </div>
                                                        )}
                                                        {msg.error && (
                                                          <div className="conversation-msg-error">
                                                            {msg.error}
                                                          </div>
                                                        )}
                                                      </div>
                                                    )
                                                  })}
                                                </div>
                                              )}
                                              {conversationFeedback[conv.id] && (
                                                <div
                                                  className={`conversation-feedback-banner ${conversationFeedback[conv.id]!.rating}`}
                                                >
                                                  <span className="conversation-feedback-icon">
                                                    {conversationFeedback[
                                                      conv.id
                                                    ]!.rating === 'thumbs_up'
                                                      ? '👍'
                                                      : '👎'}
                                                  </span>
                                                  <div className="conversation-feedback-detail">
                                                    <span className="conversation-feedback-label">
                                                      User rated this{' '}
                                                      {conversationFeedback[
                                                        conv.id
                                                      ]!.rating ===
                                                      'thumbs_up'
                                                        ? 'helpful'
                                                        : 'not helpful'}
                                                    </span>
                                                    {conversationFeedback[
                                                      conv.id
                                                    ]!.feedback && (
                                                      <span className="conversation-feedback-text">
                                                        &quot;
                                                        {
                                                          conversationFeedback[
                                                            conv.id
                                                          ]!.feedback
                                                        }
                                                        &quot;
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>
                                              )}
                                              {msgs && msgs.length === 0 && (
                                                <div className="conversation-loading">
                                                  No messages found.
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )
                                    })}
                                  {convData &&
                                    convData.data.length === 0 && (
                                      <div className="conversation-loading">
                                        No conversations found for this app.
                                      </div>
                                    )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
            {perAppTotalPages > 1 && (
              <div className="ai-pagination">
                <div className="pagination-info">
                  Page {perAppPage} of {perAppTotalPages} (
                  {engagement.per_app_pagination.total} total)
                </div>
                <div className="pagination-controls">
                  <button
                    type="button"
                    disabled={perAppPage === 1 || loadingPerApp}
                    onClick={() => handlePerAppPageChange(1)}
                  >
                    First
                  </button>
                  <button
                    type="button"
                    disabled={perAppPage === 1 || loadingPerApp}
                    onClick={() => handlePerAppPageChange(perAppPage - 1)}
                  >
                    Prev
                  </button>
                  <span className="page-info">
                    {perAppPage} / {perAppTotalPages}
                  </span>
                  <button
                    type="button"
                    disabled={
                      perAppPage === perAppTotalPages || loadingPerApp
                    }
                    onClick={() => handlePerAppPageChange(perAppPage + 1)}
                  >
                    Next
                  </button>
                  <button
                    type="button"
                    disabled={
                      perAppPage === perAppTotalPages || loadingPerApp
                    }
                    onClick={() => handlePerAppPageChange(perAppTotalPages)}
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Error Rate Section ────────────────────────────────── */}
      <div className="analytics-section">
        <h2 className="section-title">Error Rate Monitoring</h2>

        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-label">Total Requests</div>
            <div className="stat-value">
              {errorRate?.summary?.total_requests ?? 0}
            </div>
          </div>
          <div className="stat-card success">
            <div className="stat-label">Successful</div>
            <div className="stat-value">
              {errorRate?.summary?.successful_requests ?? 0}
            </div>
          </div>
          <div className="stat-card danger">
            <div className="stat-label">Errors</div>
            <div className="stat-value">
              {errorRate?.summary?.error_requests ?? 0}
            </div>
          </div>
          <div className="stat-card warning">
            <div className="stat-label">Error Rate</div>
            <div className="stat-value">
              {errorRate?.summary?.error_rate_pct ?? 0}%
            </div>
          </div>
        </div>

        <div className="charts-container">
          <div className="chart-container">
            <h2>Daily Error Rate</h2>
            <p>Last {days} days</p>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart
                data={errorRate?.daily || []}
                margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis
                  yAxisId="count"
                  orientation="left"
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  yAxisId="pct"
                  orientation="right"
                  unit="%"
                  tick={{ fontSize: 11 }}
                />
                <Tooltip />
                <Legend />
                <Line
                  yAxisId="count"
                  type="monotone"
                  dataKey="successful_requests"
                  stroke="#10b981"
                  name="Successful"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  yAxisId="count"
                  type="monotone"
                  dataKey="error_requests"
                  stroke="#ef4444"
                  name="Errors"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  yAxisId="pct"
                  type="monotone"
                  dataKey="error_rate_pct"
                  stroke="#f59e0b"
                  name="Error Rate %"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Errors Table */}
        {errorRate?.top_errors && errorRate.top_errors.length > 0 && (
          <div className="analytics-table-wrapper">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Error</th>
                  <th style={{ textAlign: 'right' }}>Occurrences</th>
                </tr>
              </thead>
              <tbody>
                {errorRate.top_errors.map(e => (
                  <tr key={e.error_snippet}>
                    <td
                      style={{
                        maxWidth: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={e.error_snippet}
                    >
                      {e.error_snippet}
                    </td>
                    <td className="numeric">{e.occurrences}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Satisfaction Section ──────────────────────────────── */}
      <div className="analytics-section">
        <h2 className="section-title">Satisfaction & Feedback</h2>

        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-label">Total Ratings</div>
            <div className="stat-value">
              {satisfaction?.overall?.total_ratings ?? 0}
            </div>
          </div>
          <div className="stat-card success">
            <div className="stat-label">Thumbs Up</div>
            <div className="stat-value">
              {satisfaction?.overall?.thumbs_up_count ?? 0}
            </div>
          </div>
          <div className="stat-card danger">
            <div className="stat-label">Thumbs Down</div>
            <div className="stat-value">
              {satisfaction?.overall?.thumbs_down_count ?? 0}
            </div>
          </div>
          <div className="stat-card success">
            <div className="stat-label">Satisfaction Rate</div>
            <div className="stat-value">
              {satisfaction?.overall?.satisfaction_rate_pct ?? 0}%
            </div>
          </div>
        </div>

        <div className="charts-container">
          <div className="chart-container">
            <h2>Daily Satisfaction Trend</h2>
            <p>Last {days} days</p>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart
                data={satisfaction?.daily || []}
                margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="thumbs_up"
                  fill="#10b981"
                  name="Thumbs Up"
                  stackId="a"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="thumbs_down"
                  fill="#ef4444"
                  name="Thumbs Down"
                  stackId="a"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-container full-width">
            <h2>Weekly Unique Users</h2>
            <p>Last {weeklyUsers?.weeks ?? 0} weeks</p>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart
                data={weeklyUsersSeries}
                margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week_label" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="total_users"
                  fill="#6366f1"
                  name="Total Users"
                />
                <Bar dataKey="new_users" fill="#10b981" name="New Users" />
                <Bar
                  dataKey="churned_users"
                  fill="#f59e0b"
                  name="Did Not Return"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Feedback List */}
        <div ref={feedbackListRef}>
          <div className="feedback-tabs">
            <button
              type="button"
              className={`feedback-tab ${feedbackTab === 'all' ? 'active' : ''}`}
              onClick={() => handleFeedbackTabChange('all')}
            >
              All
              {feedbackList && (
                <span className="tab-count">({feedbackAllCount})</span>
              )}
            </button>
            <button
              type="button"
              className={`feedback-tab ${feedbackTab === 'thumbs_up' ? 'active' : ''}`}
              onClick={() => handleFeedbackTabChange('thumbs_up')}
            >
              Thumbs Up
              {feedbackList && feedbackThumbsUpCount !== undefined && (
                <span className="tab-count">({feedbackThumbsUpCount})</span>
              )}
            </button>
            <button
              type="button"
              className={`feedback-tab ${feedbackTab === 'thumbs_down' ? 'active' : ''}`}
              onClick={() => handleFeedbackTabChange('thumbs_down')}
            >
              Thumbs Down
              {feedbackList && feedbackThumbsDownCount !== undefined && (
                <span className="tab-count">({feedbackThumbsDownCount})</span>
              )}
            </button>
          </div>

          {loadingFeedback && (
            <div className="analytics-table-wrapper">
              <table className="analytics-table">
                <tbody>
                  <tr className="loading-row">
                    <td>
                      <div className="conversation-loading">
                        Loading feedback...
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {!loadingFeedback &&
            feedbackList?.data &&
            feedbackList.data.length > 0 && (
            <>
              {feedbackList.data.map(item => {
                const isExpanded = expandedConversation === item.conversationId
                const messages = conversationMessages[item.conversationId]
                const isLoadingMsgs =
                  loadingConversation === item.conversationId

                return (
                  <div
                    className={`feedback-card ${isExpanded ? 'expanded' : ''}`}
                    key={item.id}
                  >
                    <div
                      className="feedback-card-header"
                      onClick={() => {
                        handleToggleConversation(item.conversationId).catch(
                          err => console.error(err)
                        )
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          // eslint-disable-next-line @typescript-eslint/no-floating-promises
                          handleToggleConversation(item.conversationId)
                        }
                      }}
                    >
                      <div className={`feedback-rating ${item.rating}`}>
                        {item.rating === 'thumbs_up' ? '👍' : '👎'}
                      </div>
                      <div className="feedback-content">
                        <div className="feedback-meta">
                          <span>
                            <strong>{item.user_name || 'Unknown'}</strong> (
                            {item.user_email || '—'})
                          </span>
                          <span>
                            App:{' '}
                            <strong>{item.app_name || item.appId}</strong>
                          </span>
                          <span>
                            {new Date(item.createdAt).toLocaleDateString(
                              'en-US',
                              {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              }
                            )}
                          </span>
                        </div>
                        {item.feedback ? (
                          <div className="feedback-text">
                            {item.feedback}
                          </div>
                        ) : (
                          <div className="no-feedback-text">
                            No written feedback provided
                          </div>
                        )}
                      </div>
                      <div
                        className={`feedback-expand-icon ${isExpanded ? 'open' : ''}`}
                      >
                        &#9662;
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="conversation-thread">
                        {isLoadingMsgs && (
                          <div className="conversation-loading">
                            Loading conversation...
                          </div>
                        )}
                        {messages && messages.length > 0 && (
                          <div className="conversation-messages">
                            {messages.map((msg, idx) => {
                              const statuses = parseStatuses(msg.statuses)
                              const responseSeconds = getResponseTimeSeconds(
                                messages,
                                idx
                              )

                              return (
                                <div
                                  key={msg.id}
                                  className={`conversation-msg ${msg.role}`}
                                >
                                  <div className="conversation-msg-header">
                                    <span
                                      className={`conversation-msg-role ${msg.role}`}
                                    >
                                      {msg.role === 'user'
                                        ? 'User'
                                        : 'AI Assistant'}
                                    </span>
                                    <span className="conversation-msg-time">
                                      {new Date(
                                        msg.createdAt
                                      ).toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                    </span>
                                    {responseSeconds !== null && (
                                      <span
                                        className={`conversation-msg-response-time ${getResponseTimeClass(
                                          responseSeconds
                                        )}`}
                                      >
                                        Response:{' '}
                                        {formatResponseTime(responseSeconds)}
                                      </span>
                                    )}
                                    {msg.error && (
                                      <span className="conversation-msg-error-badge">
                                        Error
                                      </span>
                                    )}
                                  </div>
                                  <div className="conversation-msg-body">
                                    {msg.message}
                                  </div>
                                  {statuses.length > 0 && (
                                    <div className="conversation-msg-statuses">
                                      {statuses.map((s) => (
                                        <span
                                          key={`${msg.id}-status-${s.type}-${s.message.slice(0, 20)}`}
                                          className={`conversation-status-badge status-${s.type}`}
                                        >
                                          {s.type === 'success' && '✓ '}
                                          {s.type === 'error' && '✕ '}
                                          {s.type === 'info' && 'ℹ '}
                                          {s.message}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {msg.error && (
                                    <div className="conversation-msg-error">
                                      {msg.error}
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                        {messages && messages.length === 0 && (
                          <div className="conversation-loading">
                            No messages found for this conversation.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}

              {feedbackTotalPages > 1 && (
                <div className="ai-pagination">
                  <div className="pagination-info">
                    Page {feedbackPage} of {feedbackTotalPages} (
                    {feedbackList.pagination.total} total)
                  </div>
                  <div className="pagination-controls">
                    <button
                      type="button"
                      disabled={feedbackPage === 1}
                      onClick={() => handleFeedbackPageChange(1)}
                    >
                      First
                    </button>
                    <button
                      type="button"
                      disabled={feedbackPage === 1}
                      onClick={() => handleFeedbackPageChange(feedbackPage - 1)}
                    >
                      Prev
                    </button>
                    <span className="page-info">
                      {feedbackPage} / {feedbackTotalPages}
                    </span>
                    <button
                      type="button"
                      disabled={feedbackPage === feedbackTotalPages}
                      onClick={() => handleFeedbackPageChange(feedbackPage + 1)}
                    >
                      Next
                    </button>
                    <button
                      type="button"
                      disabled={feedbackPage === feedbackTotalPages}
                      onClick={() =>
                        handleFeedbackPageChange(feedbackTotalPages)
                      }
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          {!loadingFeedback &&
            (!feedbackList?.data || feedbackList.data.length === 0) && (
            <div className="analytics-table-wrapper">
              <table className="analytics-table">
                <tbody>
                  <tr className="empty-row">
                    <td>No feedback data yet</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

        {/* ─── AI Application Creation Section ───────────────────── */}
        <div className="analytics-section">
          <h2 className="section-title">AI Application Creation</h2>
          <div className="charts-container">
            <div className="chart-container full-width">
              <h2>
                {appCreation?.bucket_type === 'daily'
                  ? 'Daily'
                  : 'Weekly'}{' '}
                Applications Created
              </h2>
              <p>
                Last {days} days ·{' '}
                {appCreation?.bucket_type === 'daily' ? 'Daily' : 'Weekly'} totals
              </p>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={appCreationSeries}
                  margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="total" fill="#6366f1" name="Total" />
                  <Bar dataKey="successful" fill="#10b981" name="Successful" />
                  <Bar
                    dataKey="unsuccessful"
                    fill="#ef4444"
                    name="Unsuccessful"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </Page>
  )
}

export default AIAnalytics
